import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Container, SectionLabel } from "./ui-kit";
import CampaignCard from "./CampaignCard";
import { Globe2, MapPin } from "lucide-react";

const RIMG = (id) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400`;

const REGIONS = [
  { key: "middle-east", label: "Middle East", image: RIMG("1584758876461-054ae11c366d"), match: ["united arab emirates", "uae", "middle east", "saudi", "qatar", "oman"] },
  { key: "south-asia", label: "South Asia", image: RIMG("1636202339022-7d67f7447e3a"), match: ["india", "pakistan", "bangladesh", "south asia", "nepal", "sri lanka"] },
  { key: "africa", label: "Africa", image: RIMG("1760873059715-7c7cfbe2a2c6"), match: ["sudan", "africa", "sub-saharan", "somalia", "nigeria", "kenya"] },
  { key: "southeast-asia", label: "Southeast Asia", image: RIMG("1774437556821-239dfa4f5c7d"), match: ["indonesia", "malaysia", "southeast asia", "philippines"] },
  { key: "europe", label: "Europe", image: RIMG("1628717341663-0007b0ee2597"), match: ["europe", "uk", "united kingdom", "france", "germany"] },
  { key: "north-america", label: "North America", image: RIMG("1714974528737-3e6c7e4d11af"), match: ["united states", "usa", "canada", "north america"] },
  { key: "oceania", label: "Oceania", image: RIMG("1533222535026-754c501569dd"), match: ["australia", "oceania", "new zealand"] },
];

export default function OneUmmahMap() {
  const [campaigns, setCampaigns] = useState([]);
  const [active, setActive] = useState("middle-east");

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const countByRegion = useMemo(() => {
    const out = {};
    for (const r of REGIONS) {
      out[r.key] = campaigns.filter((c) => {
        const country = (c.country || "").toLowerCase();
        if (country.includes("multiple")) return true;
        return r.match.some((m) => country.includes(m));
      });
    }
    return out;
  }, [campaigns]);

  const shown = countByRegion[active] || [];

  return (
    <section className="bg-white">
      <Container className="py-16 lg:py-24">
        <div className="overflow-hidden rounded-3xl islamic-pattern text-white">
          <div className="grid gap-8 p-8 lg:grid-cols-[1.1fr_1fr] lg:p-12">
            <div>
              <SectionLabel light>One Ummah</SectionLabel>
              <h2 className="mt-4 font-display text-4xl font-bold lg:text-5xl">One Ummah. Many communities.<br />One responsibility.</h2>
              <p className="mt-4 max-w-xl text-emerald-50/80">Select a region to reveal the initiatives we coordinate there with verified partners. We never fabricate numbers — regions without active programmes are shown honestly.</p>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {REGIONS.map((r) => {
                  const n = (countByRegion[r.key] || []).length;
                  const isActive = active === r.key;
                  return (
                    <button
                      key={r.key}
                      onClick={() => setActive(r.key)}
                      data-testid={`region-${r.key}`}
                      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${isActive ? "border-gold bg-gold text-white" : "border-white/25 text-emerald-50/90 hover:border-gold/60 hover:bg-white/10"}`}
                    >
                      <MapPin className="h-3.5 w-3.5" /> {r.label}
                      <span className={`rounded-full px-1.5 text-xs ${isActive ? "bg-white/25" : "bg-white/10"}`}>{n}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="relative flex items-center justify-center">
              <div className="relative flex h-56 w-56 items-center justify-center rounded-full border border-gold/30 sm:h-72 sm:w-72" style={{ background: "radial-gradient(circle at 30% 30%, rgba(212,169,58,0.18), transparent 60%), radial-gradient(circle at 1px 1px, rgba(212,169,58,0.25) 1px, transparent 0)", backgroundSize: "auto, 14px 14px" }}>
                <Globe2 className="h-20 w-20 text-gold-light/70" strokeWidth={1} />
                <div className="absolute inset-0 animate-[spin_28s_linear_infinite] rounded-full border border-dashed border-white/10" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10" data-testid="region-initiatives">
          <div className="relative mb-8 overflow-hidden rounded-3xl">
            <img src={REGIONS.find((r) => r.key === active)?.image} alt={REGIONS.find((r) => r.key === active)?.label} className="h-52 w-full object-cover sm:h-64" />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-light">Region Focus</p>
              <h3 className="mt-1 font-display text-3xl font-bold text-white">Initiatives in {REGIONS.find((r) => r.key === active)?.label}</h3>
            </div>
          </div>
          {shown.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-emerald-900/20 bg-sand p-12 text-center">
              <Globe2 className="mx-auto h-8 w-8 text-gold" />
              <p className="mt-3 font-display text-xl font-bold text-emerald-900">Programmes coming soon</p>
              <p className="mt-2 text-sm text-slate-500">We are developing initiatives in this region with trusted partners.</p>
            </div>
          ) : (
            <div className="mt-6 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {shown.map((c) => <CampaignCard key={c.slug} c={c} />)}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
}
